import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatUSD } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { QRCodeSVG } from "qrcode.react";
import {
  CheckCircle, Receipt, Warning, ArrowLeft, QrCode, ArrowUp, ArrowDown, Storefront, Bell,
  DeviceMobile, DownloadSimple, Plus, Lightning, Heart,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

export default function Pay() {
  const t = useT();
  const [sp] = useSearchParams();
  const last4 = (sp.get("last4") || "").replace(/\D/g, "").slice(0, 4);
  const exp = (sp.get("exp") || "").slice(0, 5);
  const qrToken = sp.get("t") || ""; // 60s anti-replay token from a fresh QR scan

  const [card, setCard] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  // Mode: "view" = receiver-friendly balance + activity (default landing)
  //       "charge" = merchant-facing charge form
  const [mode, setMode] = useState("view");
  const [amount, setAmount] = useState("");
  const [merchant, setMerchant] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (last4.length !== 4 || exp.length !== 5) {
      setLoadErr("Missing card details in URL.");
      return;
    }
    api.post("/track-card", { last4, exp })
      .then((r) => setCard(r.data))
      .catch((e) => setLoadErr(e?.response?.data?.detail || "Card not found"));

    // Fire a QR-scan analytics ping once per session per card.
    const fromScan = sp.get("s") === "scan";
    if (fromScan) {
      const key = `qr_scan_logged:${last4}:${exp}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        api.post("/track-card/scan", { last4, exp }).catch(() => {});
      }
    }
  }, [last4, exp, sp]);

  const refreshBalance = () => {
    api.post("/track-card", { last4, exp }).then((r) => setCard(r.data)).catch(() => {});
  };

  // Receiver-side balance poll (only runs while the receiver-view is mounted).
  // When the balance drops we know a charge just landed — show a tiny toast
  // so the receiver sees their cash card "ping" like an Apple Pay notification,
  // and trigger an immediate QR-token rotation so the next merchant scan
  // hits a fresh, unused token (silent recovery from accidental double-scans).
  useEffect(() => {
    if (!card || mode !== "view" || receipt) return;
    let prevBalance = card.balance;
    const id = setInterval(async () => {
      try {
        const r = await api.post("/track-card", { last4, exp });
        const next = r.data;
        if (typeof next?.balance === "number" && next.balance < (prevBalance ?? Infinity) - 0.001) {
          const delta = (prevBalance ?? next.balance) - next.balance;
          // Cause the TapToPayCard's effect to re-run (and fetch a brand-new
          // 60s token) by toggling a refresh-key embedded in the card object.
          setCard({ ...next, _qrRefreshKey: Date.now() });
          playPaymentChime();
          toast.success(t("pay.toast.payment_received", { amount: formatUSD(delta) }));
        } else {
          setCard(next);
        }
        prevBalance = next.balance;
      } catch {
        // Ignore intermittent failures — the next tick will retry.
      }
    }, 8000);
    return () => clearInterval(id);
  }, [card?.balance, last4, exp, mode, receipt, card]);

  const charge = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const r = await api.post("/track-card/charge", {
        last4,
        exp,
        amount_usd: Number(amount),
        merchant_name: merchant.trim() || undefined,
        memo: memo.trim() || undefined,
        qr_token: qrToken || undefined,
      });
      setReceipt(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || t("pay.error.charge_failed"));
    } finally {
      setBusy(false);
    }
  };

  const resetForAnother = () => {
    setReceipt(null);
    setAmount("");
    setMemo("");
    refreshBalance();
    setMode("view");
  };

  if (loadErr) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header />
        <div className="max-w-md mx-auto px-6 py-20 text-center">
          <Warning size={48} weight="duotone" className="mx-auto text-[#D96C4A]" />
          <h1 className="serif text-3xl mt-4">{t("pay.error.cant_load")}</h1>
          <p className="text-[#4A5C50] mt-3">{loadErr}</p>
          <Link to="/" className="link-ul text-sm tracking-widest uppercase mt-6 inline-block">{t("pay.error.back_home")}</Link>
        </div>
      </div>
    );
  }

  if (!card) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Header />
        <div className="max-w-md mx-auto px-6 py-20 text-[#4A5C50]">{t("pay.error.loading")}</div>
      </div>
    );
  }

  const isPaused = card.status === "paused";

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-md mx-auto px-6 py-12">
        {mode === "charge" && (
          <button
            type="button"
            onClick={() => { setMode("view"); setErr(""); }}
            className="inline-flex items-center gap-2 text-sm text-[#4A5C50] link-ul"
            data-testid="pay-back-to-view"
          >
            <ArrowLeft size={14} /> {t("pay.back_to_balance")}
          </button>
        )}

        {/* Header band */}
        <div className="flex items-center gap-3 mt-4">
          <QrCode size={22} weight="duotone" className="text-[#D96C4A]" />
          <p className="text-xs tracking-[0.3em] uppercase text-[#4A5C50]" data-testid="pay-mode-label">
            {mode === "view" ? t("pay.mode.view") : t("pay.mode.charge")}
          </p>
        </div>

        <h1 className="serif text-4xl md:text-5xl mt-3 leading-[1.05]">
          {mode === "view" ? (
            t("pay.heading.view", { name: card.cardholder.split(" ")[0] })
          ) : (
            t("pay.heading.charge")
          )}
        </h1>
        <p className="text-sm text-[#4A5C50] mono mt-3" data-testid="pay-masked">{card.masked_number}</p>
        <p className="text-sm text-[#4A5C50]">{t("pay.cardholder")} · {card.cardholder}</p>

        {isPaused && (
          <div className="mt-6 rounded-xl border border-[#D96C4A]/40 bg-[#D96C4A]/10 p-5" data-testid="pay-paused-banner">
            <p className="text-xs tracking-[0.3em] uppercase text-[#B04E2F]">{t("pay.paused.label")}</p>
            <p className="text-sm text-[#112318] mt-2">
              {t("pay.paused.body")}
            </p>
          </div>
        )}

        {/* Balance card — shown in BOTH modes */}
        <div className="mt-6 rounded-2xl bg-[#112318] text-[#FBF9F6] p-6" data-testid="pay-balance-card">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("pay.balance.label")}</p>
          <p className="serif text-5xl mt-1" data-testid="pay-balance">{formatUSD(card.balance)}</p>
          <p className="mono text-[11px] opacity-50 mt-3">
            {(card.charges_count === 1 ? t("pay.charges_count", { count: card.charges_count || 0 }) : t("pay.charges_count_plural", { count: card.charges_count || 0 }))} · {(card.topups_count === 1 ? t("pay.topups_count", { count: card.topups_count || 0 }) : t("pay.topups_count_plural", { count: card.topups_count || 0 }))}
          </p>
        </div>

        {/* RECEIVER VIEW (default) */}
        {mode === "view" && !receipt && (
          <ReceiverActivityView
            card={{ ...card, exp }}
            isPaused={isPaused}
            onChargeClick={() => setMode("charge")}
            t={t}
          />
        )}

        {/* CHARGE FORM (merchant flow) */}
        {mode === "charge" && !receipt && (
          <form onSubmit={charge} className="mt-8 space-y-5" data-testid="pay-form">
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("pay.form.amount_label")}</Label>
              <input
                type="number"
                inputMode="decimal"
                min="0.01"
                max={card.balance}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                required
                autoFocus
                className="underline-input mono text-4xl"
                data-testid="pay-amount"
              />
              <p className="text-[11px] text-[#4A5C50] mt-1">{t("pay.form.amount_max", { amount: formatUSD(card.balance) })}</p>
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("pay.form.merchant_label")}</Label>
              <input
                type="text"
                value={merchant}
                onChange={(e) => setMerchant(e.target.value)}
                placeholder={t("pay.form.merchant_placeholder")}
                maxLength={80}
                className="underline-input text-lg"
                data-testid="pay-merchant"
              />
            </div>
            <div>
              <Label className="text-xs tracking-widest uppercase text-[#4A5C50]">{t("pay.form.memo_label")}</Label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder={t("pay.form.memo_placeholder")}
                maxLength={200}
                className="underline-input text-base"
                data-testid="pay-memo"
              />
            </div>
            {err && <p className="text-sm text-[#D96C4A]" data-testid="pay-error">{err}</p>}
            <Button
              type="submit"
              disabled={busy || isPaused || !amount || Number(amount) <= 0 || Number(amount) > card.balance}
              className="w-full h-14 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-base disabled:opacity-50"
              data-testid="pay-submit"
            >
              {busy ? t("pay.form.submit_processing") : isPaused ? t("pay.form.submit_paused") : (amount ? t("pay.form.submit_charge", { amount: formatUSD(Number(amount) || 0) }) : t("pay.form.submit_charge_card"))}
            </Button>
            <p className="text-[11px] text-[#4A5C50] text-center mt-2">
              {t("pay.form.disclaimer")}
            </p>
          </form>
        )}

        {/* SHARED RECEIPT — shown after a successful charge in either mode */}
        {receipt && (
          <div className="mt-8 rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-6" data-testid="pay-receipt">
            <div className="flex items-center gap-2 text-[#184A30]">
              <CheckCircle size={24} weight="fill" />
              <p className="text-xs tracking-[0.3em] uppercase">{t("pay.receipt.title")}</p>
            </div>
            <p className="serif text-4xl mt-3 text-[#184A30]" data-testid="pay-receipt-amount">
              − {formatUSD(receipt.amount_usd)}
            </p>
            <p className="text-sm text-[#4A5C50] mt-1">{t("pay.receipt.to")} <b className="text-[#112318]">{receipt.merchant_name}</b></p>
            <div className="mt-4 pt-4 border-t border-[#184A30]/10 text-xs mono text-[#4A5C50] space-y-1">
              <p>{t("pay.receipt.id")} · <span className="text-[#112318]">{receipt.charge_id.slice(0, 8)}</span></p>
              <p>{t("pay.receipt.time")} · {new Date(receipt.ts).toLocaleString()}</p>
              <p>{t("pay.receipt.new_balance")} · <span className="text-[#184A30] font-semibold" data-testid="pay-receipt-balance">{formatUSD(receipt.new_balance)}</span></p>
            </div>
            <Button
              onClick={resetForAnother}
              className="mt-6 w-full h-12 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
              data-testid="pay-again-btn"
            >
              <Receipt size={18} className="mr-2" /> {t("pay.receipt.done")}
            </Button>
            {receipt.can_thank_sender && (
              <ThankYouPanel
                last4={last4}
                exp={exp}
                chargeId={receipt.charge_id}
                amountUsd={receipt.amount_usd}
                t={t}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}


function ReceiverActivityView({ card, isPaused, onChargeClick, t }) {
  // Merge recent topups + charges into a single newest-first feed
  const feed = useMemo(() => {
    const items = [];
    (card.recent_topups || []).forEach((tp) =>
      items.push({ kind: "topup", ts: tp.ts, amount: tp.amount_usd, label: methodLabel(tp.method, t), id: tp.id })
    );
    (card.recent_charges || []).forEach((c) =>
      items.push({ kind: "charge", ts: c.ts, amount: c.amount_usd, label: c.merchant || t("pay.activity.merchant_charge"), sub: c.memo, id: c.id })
    );
    items.sort((a, b) => new Date(b.ts) - new Date(a.ts));
    return items.slice(0, 8);
  }, [card.recent_topups, card.recent_charges, t]);

  return (
    <>
      {/* Apple-Pay style "Tap to Pay" card */}
      <TapToPayCard card={card} isPaused={isPaused} t={t} />

      <div className="mt-8" data-testid="receiver-activity">
        <div className="flex items-end justify-between mb-3">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{t("pay.activity.label")}</p>
          {feed.length === 0 && <p className="text-xs text-[#4A5C50]">{t("pay.activity.empty")}</p>}
        </div>
        {feed.length > 0 && (
          <ol className="bg-white border border-[#E5E0D8] rounded-2xl divide-y divide-[#E5E0D8]">
            {feed.map((e, idx) => (
              <li
                key={`${e.kind}-${e.id || idx}-${e.ts}`}
                className="flex items-center gap-3 p-4"
                data-testid={`activity-${e.kind}-${idx}`}
              >
                <span
                  className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white ${
                    e.kind === "topup" ? "bg-[#184A30]" : "bg-[#D96C4A]"
                  }`}
                  aria-hidden="true"
                >
                  {e.kind === "topup" ? <ArrowUp size={14} weight="bold" /> : <Storefront size={14} weight="duotone" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#112318] truncate">{e.label}</p>
                  <p className="text-[11px] text-[#4A5C50] mono mt-0.5">
                    {e.ts ? new Date(e.ts).toLocaleString() : ""}
                    {e.sub ? <> · {e.sub}</> : null}
                  </p>
                </div>
                <p
                  className={`mono font-semibold text-sm shrink-0 ${
                    e.kind === "topup" ? "text-[#184A30]" : "text-[#D96C4A]"
                  }`}
                >
                  {e.kind === "topup" ? "+" : "−"} {formatUSD(e.amount)}
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* Merchant charge CTA + receiver-friendly explainer */}
      <div className="mt-8 space-y-3" data-testid="receiver-cta">
        <Button
          type="button"
          onClick={onChargeClick}
          disabled={isPaused || (card.balance ?? 0) <= 0}
          className="w-full h-14 rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white text-base disabled:opacity-50"
          data-testid="open-charge-btn"
        >
          <ArrowDown size={18} className="mr-2" weight="bold" /> {isPaused ? t("pay.cta.charge_paused") : t("pay.cta.charge")}
        </Button>
        <p className="text-[11px] text-[#4A5C50] text-center">
          {isPaused ? t("pay.cta.note_paused") : t("pay.cta.note_active")}
        </p>
      </div>

      <SubscribeNotificationsCard last4={card.masked_number?.slice(-4)} t={t} />
      <SaveCardToPhoneCard last4={card.masked_number?.slice(-4)} t={t} />
    </>
  );
}


function SaveCardToPhoneCard({ last4, t }) {
  const [sp] = useSearchParams();
  const urlLast4 = (sp.get("last4") || "").replace(/\D/g, "").slice(0, 4);
  const exp = (sp.get("exp") || "").slice(0, 5);
  const [busy, setBusy] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  // Detect iOS Safari (supports Add to Home Screen but not native install prompt)
  const isStandalone = typeof window !== "undefined" && (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator?.standalone);
  const isIos = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const saveCardImage = async () => {
    setBusy(true);
    try {
      // Build the image URL: the existing public-card-image endpoint (signed) for THIS card.
      // We use the receiver-friendly track lookup since the receiver is unauthenticated.
      // Fallback: request the public image via last4+exp lookup endpoint.
      const r = await api.post("/track-card/image-url", { last4: urlLast4, exp });
      const path = r?.data?.path;
      const url = path ? `${window.location.origin}${path}` : r?.data?.url;
      if (!url) throw new Error("No image URL returned");

      // Try Web Share API with file (native iOS/Android share sheet → "Save Image")
      const sharedViaApi = await tryShareImage(url, last4 || urlLast4);
      if (!sharedViaApi) {
        const win = window.open(url, "_blank", "noopener");
        if (!win || win.closed) window.location.assign(url);
        toast.success(t("pay.save.toast.opened"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pay.save.toast.failed"));
    } finally {
      setBusy(false);
    }
  };

  if (isStandalone) return null;

  return (
    <div className="mt-6 rounded-2xl border border-[#CBA35C]/40 bg-[#CBA35C]/10 p-5" data-testid="save-to-phone-card">
      <div className="flex items-center gap-2 text-[#8C6B21]">
        <DeviceMobile size={16} weight="duotone" />
        <p className="text-[10px] tracking-[0.3em] uppercase">{t("pay.save.eyebrow")}</p>
      </div>
      <p className="serif text-xl text-[#112318] mt-1.5">{t("pay.save.title")}</p>
      <p className="text-xs text-[#4A5C50] mt-1.5 leading-relaxed">
        {t("pay.save.desc")}
      </p>

      <div className="mt-4 flex flex-wrap gap-3">
        <Button
          type="button"
          onClick={saveCardImage}
          disabled={busy}
          className="rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white text-sm px-4 disabled:opacity-50"
          data-testid="save-card-image-btn"
        >
          <DownloadSimple size={16} className="mr-1.5" weight="bold" />
          {busy ? t("pay.save.preparing") : t("pay.save.cta")}
        </Button>

        {isIos && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowIosHint((v) => !v)}
            className="rounded-lg border-[#184A30] text-[#184A30] text-sm px-4"
            data-testid="ios-add-home-btn"
          >
            <Plus size={16} className="mr-1.5" weight="bold" />
            {t("pay.save.add_home")}
          </Button>
        )}
      </div>

      {showIosHint && (
        <div className="mt-4 rounded-lg bg-white border border-[#184A30]/15 p-3 text-xs text-[#4A5C50] leading-relaxed" data-testid="ios-hint">
          <p className="font-semibold text-[#112318] mb-1">{t("pay.save.ios.title")}</p>
          <ol className="list-decimal list-inside space-y-0.5">
            <li>{t("pay.save.ios.step1")}</li>
            <li>{t("pay.save.ios.step2")}</li>
            <li>{t("pay.save.ios.step3")}</li>
          </ol>
          <p className="mt-2 opacity-70">{t("pay.save.ios.note")}</p>
        </div>
      )}
    </div>
  );
}


async function tryShareImage(imageUrl, last4) {
  if (typeof navigator === "undefined" || !navigator.share || !navigator.canShare) return false;
  try {
    const resp = await fetch(imageUrl);
    if (!resp.ok) return false;
    const blob = await resp.blob();
    const file = new File([blob], `zcardon-${last4 || "card"}.png`, { type: blob.type || "image/png" });
    if (!navigator.canShare({ files: [file] })) return false;
    await navigator.share({
      title: `Zcardon · card ending ${last4 || ""}`,
      text: "My Zcardon virtual card",
      files: [file],
    });
    return true;
  } catch (e) {
    // User canceled or browser doesn't support — fall back to download
    return false;
  }
}


function SubscribeNotificationsCard({ last4, t }) {
  const [sp] = useSearchParams();
  const urlLast4 = (sp.get("last4") || "").replace(/\D/g, "").slice(0, 4);
  const exp = (sp.get("exp") || "").slice(0, 5);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/track-card/subscribe", { last4: urlLast4, exp, email: email.trim() });
      setDone(true);
      if (r.data?.already_subscribed) {
        toast.success(t("pay.subscribe.toast.already"));
      } else {
        toast.success(t("pay.subscribe.toast.success"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("pay.subscribe.toast.failed"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-6 rounded-2xl border border-[#184A30]/30 bg-[#184A30]/5 p-5 flex items-start gap-3"
        data-testid="subscribe-success"
      >
        <CheckCircle size={22} weight="fill" className="text-[#184A30] shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-[#112318]">{t("pay.subscribe.done.title")}</p>
          <p className="text-xs text-[#4A5C50] mt-1">
            {t("pay.subscribe.done.body", { email, last4: last4 || urlLast4 })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5"
      data-testid="subscribe-form"
    >
      <div className="flex items-center gap-2 text-[#184A30]">
        <Bell size={16} weight="duotone" />
        <p className="text-[10px] tracking-[0.3em] uppercase">{t("pay.subscribe.eyebrow")}</p>
      </div>
      <p className="serif text-xl text-[#112318] mt-1.5">{t("pay.subscribe.title")}</p>
      <p className="text-xs text-[#4A5C50] mt-1.5 leading-relaxed">
        {t("pay.subscribe.desc")}
      </p>
      <div className="mt-4 flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("pay.subscribe.placeholder")}
          className="underline-input text-base flex-1"
          data-testid="subscribe-email"
        />
        <Button
          type="submit"
          disabled={busy || !email.includes("@")}
          className="rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-white px-4 disabled:opacity-50"
          data-testid="subscribe-submit"
        >
          {busy ? t("pay.subscribe.sending") : t("pay.subscribe.cta")}
        </Button>
      </div>
    </form>
  );
}


function methodLabel(method, t) {
  const m = (method || "").toLowerCase();
  if (m.includes("bank")) return t ? t("pay.activity.topup_bank") : "Bank transfer top-up";
  if (m.includes("debit")) return t ? t("pay.activity.topup_debit") : "Debit card top-up";
  if (m.includes("credit")) return t ? t("pay.activity.topup_credit") : "Credit card top-up";
  if (m.includes("crypto")) return t ? t("pay.activity.topup_crypto") : "Crypto top-up";
  return t ? t("pay.activity.topup_received") : "Top-up received";
}


// Plays an Apple-Pay-style "ding" without needing any asset file. Two tiny
// sine notes (~0.18s + ~0.22s) layered with a fast attack/decay envelope.
// Honors the user's prefers-reduced-motion preference and silently fails on
// browsers that block AudioContext (e.g. Safari before user interaction).
function playPaymentChime() {
  if (typeof window === "undefined") return;
  try {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    if (sessionStorage.getItem("zcardon_mute_chime") === "1") return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const t0 = ctx.currentTime;
    const note = (freq, start, dur, peak = 0.18) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0 + start);
      gain.gain.setValueAtTime(0, t0 + start);
      gain.gain.linearRampToValueAtTime(peak, t0 + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + start);
      osc.stop(t0 + start + dur + 0.05);
    };
    note(880, 0,    0.18); // A5
    note(1318, 0.12, 0.22); // E6 (perfect-fifth-up — chimey, not annoying)
    setTimeout(() => ctx.close().catch(() => {}), 600);
  } catch {
    // Browsers that gate AudioContext behind a user gesture will throw
    // before the receiver tap-to-pay screen has any user interaction. That's
    // fine — silent failure is acceptable.
  }
}



function TapToPayCard({ card, isPaused, t }) {
  const [showFull, setShowFull] = useState(false);
  // Rotating short-lived token embedded in the QR — anti-replay defense.
  // The backend issues a 60s-TTL HMAC token; we refresh every 50s while
  // this card is on screen so the QR never goes stale.
  const [tokenInfo, setTokenInfo] = useState(null); // { token, expires_at, refresh_in_secs }
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  const [splash, setSplash] = useState(false);
  const last4 = card.masked_number?.slice(-4) || "0000";

  useEffect(() => {
    let alive = true;
    const fetchToken = async () => {
      try {
        const r = await api.post("/track-card/qr-token", { last4, exp: card.exp || "" });
        if (alive && r?.data?.token) setTokenInfo(r.data);
      } catch {
        // Static QR fallback handled below — fail silently.
      }
    };
    if (!isPaused) {
      fetchToken();
      const id = setInterval(fetchToken, 50_000);
      return () => { alive = false; clearInterval(id); };
    }
    return () => { alive = false; };
  }, [last4, card.exp, isPaused, card._qrRefreshKey]);

  // When the parent triggers a refresh-key change (i.e. a charge just landed),
  // flash the green-check splash for ~900 ms over the QR — Apple Pay's "tap
  // accepted" beat. The QR is rotated by the effect above on the same trigger.
  useEffect(() => {
    if (!card._qrRefreshKey) return;
    setSplash(true);
    const t = setTimeout(() => setSplash(false), 900);
    return () => clearTimeout(t);
  }, [card._qrRefreshKey]);

  // Tick every second so the countdown stays live.
  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-collapse revealed digits after 60s — the same transient feel as Apple Pay
  useEffect(() => {
    if (!showFull) return;
    const t = setTimeout(() => setShowFull(false), 60000);
    return () => clearTimeout(t);
  }, [showFull]);

  const secondsLeft = tokenInfo ? Math.max(0, tokenInfo.expires_at - now) : 0;
  // Build the live QR value: prefer a token-bearing URL (rotates every 50s),
  // fall back to the static qr_payload from the DB so older cards still work.
  const baseOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const qrValue = tokenInfo
    ? `${baseOrigin}/pay?last4=${last4}&exp=${encodeURIComponent(card.exp || "")}&t=${tokenInfo.token}&s=scan`
    : (card.qr_payload || (card.card_id ? `${baseOrigin}/pay?last4=${last4}&exp=&s=scan&id=${card.card_id}` : ""));

  return (
    <div className="mt-8" data-testid="tap-to-pay-card">
      <div className="relative rounded-3xl bg-gradient-to-br from-[#0A1F16] via-[#112318] to-[#1F4030] text-[#FBF9F6] p-6 overflow-hidden shadow-xl">
        {/* Decorative shimmer */}
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D96C4A] blur-3xl" />
          <div className="absolute -bottom-24 -left-12 w-72 h-72 rounded-full bg-[#8BA888] blur-3xl" />
        </div>

        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase opacity-70">
              <Lightning size={12} weight="fill" className="text-[#D96C4A]" /> {t("pay.tap.eyebrow")}
            </div>
            <p className="serif text-2xl mt-2 leading-tight">{t("pay.tap.title")}</p>
            <p className="text-xs opacity-70 mt-0.5">{t("pay.tap.subtitle")}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">{t("pay.tap.balance")}</p>
            <p className="serif text-2xl mt-1" data-testid="tap-balance">{formatUSD(card.balance)}</p>
          </div>
        </div>

        {/* QR code panel — pulsing ring when ready */}
        <div className="relative mt-6 flex flex-col items-center" data-testid="tap-qr-area">
          {qrValue ? (
            <div className={`relative bg-white rounded-2xl p-4 ${isPaused ? "opacity-40" : "tap-qr-pulse"}`}>
              <QRCodeSVG value={qrValue} size={196} fgColor="#0A1F16" bgColor="#FFFFFF" includeMargin={false} />
              {isPaused && (
                <div className="absolute inset-0 flex items-center justify-center text-[#B04E2F] font-semibold text-sm bg-white/80 rounded-2xl">
                  {t("pay.tap.paused")}
                </div>
              )}
              {splash && (
                <div
                  className="absolute inset-0 flex items-center justify-center bg-[#184A30] rounded-2xl tap-splash-pop"
                  data-testid="tap-splash"
                  aria-hidden="true"
                >
                  <CheckCircle size={88} weight="fill" className="text-white drop-shadow-lg" />
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white/10 rounded-2xl p-8 text-center text-sm opacity-70 mono">
              {t("pay.tap.qr_unavailable")}
            </div>
          )}

          {/* Live token countdown */}
          {tokenInfo && !isPaused && (
            <div className="mt-3 flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase opacity-60" data-testid="tap-token-countdown">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D96C4A] animate-pulse" />
              {t("pay.tap.refresh_in", { secs: secondsLeft })}
            </div>
          )}
        </div>

        {/* Footer with last4 + reveal toggle */}
        <div className="relative mt-5 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            className="opacity-70 hover:opacity-100 mono tracking-widest underline decoration-dotted underline-offset-4"
            data-testid="tap-toggle-digits"
          >
            {showFull ? t("pay.tap.hide_digits") : t("pay.tap.show_digits")}
          </button>
          <p className="mono opacity-70">{t("pay.tap.card_label")}&nbsp;{last4}</p>
        </div>

        {showFull && (
          <div className="relative mt-3 rounded-xl bg-black/30 border border-white/10 p-3 text-center" data-testid="tap-full-digits">
            <p className="mono text-xl tracking-[0.2em]">{card.full_number || `•••• •••• •••• ${last4}`}</p>
            <p className="text-[11px] mt-1 opacity-60">
              {card.full_number ? t("pay.tap.auto_hides") : t("pay.tap.full_only_image")}
            </p>
          </div>
        )}
      </div>

      <p className="text-[11px] text-[#4A5C50] text-center mt-3">
        {t("pay.tap.disclaimer")}
      </p>

      <style>{`
        @keyframes tap-qr-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(217, 108, 74, 0.55); }
          70% { box-shadow: 0 0 0 18px rgba(217, 108, 74, 0); }
        }
        .tap-qr-pulse {
          animation: tap-qr-pulse 1.8s ease-out infinite;
        }
        @keyframes tap-splash-pop {
          0%   { transform: scale(0.6); opacity: 0; }
          25%  { transform: scale(1.05); opacity: 1; }
          75%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.95); opacity: 0; }
        }
        .tap-splash-pop {
          animation: tap-splash-pop 900ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
      `}</style>
    </div>
  );
}


function ThankYouPanel({ last4, exp, chargeId, amountUsd, t }) {
  const [emoji, setEmoji] = useState("🙏");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const PRESETS = ["🙏", "❤️", "🎉", "✨", "👏", "☕"];

  const send = async () => {
    setBusy(true);
    try {
      await api.post("/track-card/thank-you", {
        last4, exp,
        charge_id: chargeId,
        message: message.trim() || null,
        emoji,
      });
      setDone(true);
      toast.success(t("pay.thanks.toast.sent"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("pay.thanks.toast.failed"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="mt-4 rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/5 p-4 text-center text-[#B04E2F]"
        data-testid="pay-thankyou-done"
      >
        <Heart size={20} weight="fill" className="inline mr-1" />
        <span className="text-sm">{t("pay.thanks.done")}</span>
      </div>
    );
  }

  return (
    <div
      className="mt-4 rounded-xl border border-[#D96C4A]/30 bg-[#D96C4A]/5 p-4"
      data-testid="pay-thankyou-panel"
    >
      <div className="flex items-center gap-2 text-[#B04E2F]">
        <Heart size={18} weight="duotone" />
        <p className="text-xs tracking-[0.2em] uppercase">{t("pay.thanks.eyebrow")}</p>
      </div>
      <p className="text-xs text-[#4A5C50] mt-1">
        {t("pay.thanks.body", { amount: Number(amountUsd).toFixed(2) })}
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        {PRESETS.map((e) => (
          <button
            key={e}
            onClick={() => setEmoji(e)}
            className={`w-10 h-10 rounded-full border text-lg transition ${
              emoji === e
                ? "border-[#B04E2F] bg-[#D96C4A]/15"
                : "border-[#E5E0D8] bg-white hover:border-[#D96C4A]/40"
            }`}
            data-testid={`pay-thankyou-emoji-${e}`}
            type="button"
          >
            {e}
          </button>
        ))}
      </div>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value.slice(0, 240))}
        placeholder={t("pay.thanks.placeholder")}
        rows={2}
        maxLength={240}
        className="mt-3 w-full rounded-lg border border-[#E5E0D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#D96C4A]"
        data-testid="pay-thankyou-message"
      />
      <Button
        onClick={send}
        disabled={busy}
        className="mt-3 w-full h-10 rounded-xl bg-[#D96C4A] hover:bg-[#B04E2F] text-white"
        data-testid="pay-thankyou-send"
      >
        {busy ? t("pay.thanks.sending") : t("pay.thanks.cta", { emoji })}
      </Button>
    </div>
  );
}
